import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Form, InputNumber, DatePicker, Button, Space, Typography, Table, message, Alert } from 'antd';
import dayjs from 'dayjs';
import { eventoInscricaoAdminService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';

const { Text } = Typography;

// Todo calculo monetario aqui e feito em CENTAVOS (inteiros), nunca em
// float direto -- soma/divisao de reais em ponto flutuante pode gerar
// erro de arredondamento (0.1 + 0.2 != 0.3). Os valores editaveis usam
// InputNumber com precision=2 (o usuario sempre digita/ve reais), mas a
// sugestao automatica de parcelas e a validacao de soma convertem para
// centavos antes de qualquer operacao.
const paraCentavos = (valorReais) => Math.round((Number(valorReais) || 0) * 100);
const paraReais = (centavos) => centavos / 100;
const paraStringDecimal = (valorReais) => paraReais(paraCentavos(valorReais)).toFixed(2);

// Divide `totalCentavos` em `quantidade` parcelas o mais igual possivel;
// o resto da divisao inteira vai INTEIRO para a ULTIMA parcela (nunca
// espalhado) -- mesmo exemplo dado pelo produto: 250/3 = 83,33 + 83,33 +
// 83,34 (resto de 1 centavo so na ultima).
const sugerirDivisaoCentavos = (totalCentavos, quantidade) => {
  if (quantidade <= 0) return [];
  const base = Math.floor(totalCentavos / quantidade);
  const resto = totalCentavos - base * quantidade;
  const partes = new Array(quantidade).fill(base);
  partes[quantidade - 1] += resto;
  return partes;
};

const AprovacaoComercialModal = ({ open, inscricao, modo, onClose, onSalvo }) => {
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [valorBruto, setValorBruto] = useState(null);
  const [descontoReais, setDescontoReais] = useState(0);
  const [quantidadeParcelas, setQuantidadeParcelas] = useState(1);
  const [parcelas, setParcelas] = useState([]);

  useEffect(() => {
    if (!open) return;

    if (modo === 'editar') {
      setCarregandoDetalhe(true);
      eventoInscricaoAdminService
        .detalhar(inscricao.id)
        .then(({ data }) => {
          setValorBruto(Number(data.valor_bruto));
          setDescontoReais(Number(data.desconto_reais));
          setQuantidadeParcelas(data.quantidade_parcelas);
          setParcelas(
            (data.parcelas || []).map((p) => ({
              numero_parcela: p.numero_parcela,
              valor: Number(p.valor),
              data_vencimento: dayjs(p.data_vencimento),
            }))
          );
        })
        .catch((err) => handleApiError(err, 'Erro ao carregar condições comerciais'))
        .finally(() => setCarregandoDetalhe(false));
    } else {
      setValorBruto(null);
      setDescontoReais(0);
      setQuantidadeParcelas(1);
      setParcelas([]);
    }
  }, [open, modo, inscricao?.id]);

  const valorFinal = Math.max(0, paraReais(paraCentavos(valorBruto) - paraCentavos(descontoReais)));
  const valorFinalCentavos = paraCentavos(valorFinal);
  const somaParcelasCentavos = parcelas.reduce((acc, p) => acc + paraCentavos(p.valor), 0);
  const somaConfere = valorFinalCentavos === 0 ? parcelas.length === 0 : somaParcelasCentavos === valorFinalCentavos;

  const handleSugerirDivisao = () => {
    const qtd = valorFinalCentavos === 0 ? 0 : Math.max(1, quantidadeParcelas);
    setQuantidadeParcelas(qtd);
    if (qtd === 0) {
      setParcelas([]);
      return;
    }
    const partesCentavos = sugerirDivisaoCentavos(valorFinalCentavos, qtd);
    const hoje = dayjs();
    setParcelas(
      partesCentavos.map((centavos, indice) => ({
        numero_parcela: indice + 1,
        valor: paraReais(centavos),
        data_vencimento: parcelas[indice]?.data_vencimento || hoje.add(indice + 1, 'month'),
      }))
    );
  };

  const handleQuantidadeChange = (novaQuantidade) => {
    const qtd = Math.max(0, novaQuantidade || 0);
    setQuantidadeParcelas(qtd);
    setParcelas((atual) => {
      const proximo = atual.slice(0, qtd);
      while (proximo.length < qtd) {
        proximo.push({
          numero_parcela: proximo.length + 1,
          valor: 0,
          data_vencimento: dayjs().add(proximo.length + 1, 'month'),
        });
      }
      return proximo.map((p, indice) => ({ ...p, numero_parcela: indice + 1 }));
    });
  };

  const handleParcelaChange = (indice, campo, valor) => {
    setParcelas((atual) => atual.map((p, i) => (i === indice ? { ...p, [campo]: valor } : p)));
  };

  const podeSalvar =
    valorBruto !== null &&
    valorBruto >= 0 &&
    descontoReais >= 0 &&
    descontoReais <= valorBruto &&
    somaConfere &&
    parcelas.every((p) => p.valor > 0 && p.data_vencimento);

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const payload = {
        valor_bruto: paraStringDecimal(valorBruto),
        desconto_reais: paraStringDecimal(descontoReais),
        quantidade_parcelas: quantidadeParcelas,
        parcelas: parcelas.map((p) => ({
          numero_parcela: p.numero_parcela,
          valor: paraStringDecimal(p.valor),
          data_vencimento: p.data_vencimento.format('YYYY-MM-DD'),
        })),
      };
      if (modo === 'aprovar') {
        await eventoInscricaoAdminService.aprovarComercial(inscricao.id, payload);
        message.success('Inscrição aprovada com sucesso');
      } else {
        await eventoInscricaoAdminService.editarCondicoesComerciais(inscricao.id, payload);
        message.success('Condições comerciais atualizadas');
      }
      onSalvo?.();
      onClose?.();
    } catch (err) {
      handleApiError(err, 'Erro ao salvar condições comerciais');
    } finally {
      setSalvando(false);
    }
  };

  const columns = [
    { title: 'Parcela', dataIndex: 'numero_parcela', width: 90, render: (n) => `${n}/${quantidadeParcelas}` },
    {
      title: 'Valor',
      key: 'valor',
      render: (_, __, indice) => (
        <InputNumber
          min={0.01}
          precision={2}
          decimalSeparator=","
          style={{ width: 140 }}
          value={parcelas[indice]?.valor}
          onChange={(v) => handleParcelaChange(indice, 'valor', v)}
        />
      ),
    },
    {
      title: 'Vencimento',
      key: 'data_vencimento',
      render: (_, __, indice) => (
        <DatePicker
          format="DD/MM/YYYY"
          value={parcelas[indice]?.data_vencimento}
          onChange={(v) => handleParcelaChange(indice, 'data_vencimento', v)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={modo === 'aprovar' ? 'Aprovar inscrição' : 'Editar condições comerciais'}
      open={open}
      onCancel={salvando ? undefined : onClose}
      footer={
        <Space>
          <Button onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button type="primary" loading={salvando} disabled={!podeSalvar} onClick={handleSalvar}>
            {modo === 'aprovar' ? 'Salvar aprovação' : 'Salvar alterações'}
          </Button>
        </Space>
      }
      width={720}
      destroyOnClose
    >
      {inscricao && (
        <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Aluno">{inscricao.nome_aluno}</Descriptions.Item>
          <Descriptions.Item label="Turma">{inscricao.turma_origem_nome}</Descriptions.Item>
          <Descriptions.Item label="Evento">{inscricao.evento_nome}</Descriptions.Item>
          <Descriptions.Item label="Sessão">{inscricao.sessao_nome}</Descriptions.Item>
          <Descriptions.Item label="Data" span={2}>
            {dayjs(inscricao.sessao_data).format('DD/MM/YYYY')}
          </Descriptions.Item>
        </Descriptions>
      )}

      <Form layout="vertical" disabled={carregandoDetalhe}>
        <Space size="large" wrap>
          <Form.Item label="Valor da inscrição (R$)">
            <InputNumber min={0} precision={2} decimalSeparator="," style={{ width: 160 }} value={valorBruto} onChange={setValorBruto} />
          </Form.Item>
          <Form.Item label="Desconto (R$)">
            <InputNumber min={0} precision={2} decimalSeparator="," style={{ width: 160 }} value={descontoReais} onChange={setDescontoReais} />
          </Form.Item>
          <Form.Item label="Valor final">
            <Text strong style={{ fontSize: 16 }}>
              R$ {valorFinal.toFixed(2).replace('.', ',')}
            </Text>
          </Form.Item>
        </Space>

        {valorFinalCentavos > 0 && (
          <>
            <Space align="end" style={{ marginBottom: 12 }}>
              <Form.Item label="Quantidade de parcelas" style={{ marginBottom: 0 }}>
                <InputNumber min={1} precision={0} style={{ width: 120 }} value={quantidadeParcelas} onChange={handleQuantidadeChange} />
              </Form.Item>
              <Button onClick={handleSugerirDivisao}>Sugerir divisão automática</Button>
            </Space>

            <Table
              size="small"
              rowKey="numero_parcela"
              dataSource={parcelas}
              columns={columns}
              pagination={false}
              style={{ marginBottom: 12 }}
            />

            {!somaConfere && (
              <Alert
                type="warning"
                showIcon
                message={`A soma das parcelas (R$ ${(somaParcelasCentavos / 100).toFixed(2).replace('.', ',')}) precisa ser igual ao valor final (R$ ${valorFinal.toFixed(2).replace('.', ',')}).`}
                style={{ marginBottom: 12 }}
              />
            )}
          </>
        )}

        {valorFinalCentavos === 0 && valorBruto !== null && (
          <Alert
            type="info"
            showIcon
            message="Valor final igual a zero (desconto integral) -- nenhuma parcela será gerada."
            style={{ marginBottom: 12 }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default AprovacaoComercialModal;
